import { useState, type ReactNode } from "react";
import { Paperclip } from "lucide-react";
import { ApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { AttachmentGrid, AttachmentTile } from "@/components/AttachmentTile";
import { BusyIndicator } from "@/components/BusyIndicator";
import {
  appendMarkdown,
  RichTextEditor,
  toEditorMarkdown,
  type InlineImage,
  type InlineImagePreviewer,
  type InlineImageUploader,
} from "@/components/RichTextEditor";
import {
  deflateInlineImages,
  refreshInlineImages,
  URL_REFRESH_MARGIN_MS,
  inlineImageIds,
} from "@/utils/inlineImages";
import {
  conversations as conversationsApi,
  MESSAGE_BODY_MAX,
  type ConversationMessage,
} from "@/features/conversations/api/conversations.api";
import {
  MESSAGE_EDIT_ERROR,
  MESSAGE_TOO_LONG_ERROR,
} from "@/features/conversations/messageCopy";
import { ATTACHMENT_ACCEPT } from "@/features/conversations/attachments";
import { useAttachmentDraft } from "@/features/conversations/useAttachmentDraft";

export function MessageEditor({
  message,
  rows = 4,
  insertControl,
  uploadImage,
  previewImage,
  onCancel,
  onSaved,
}: {
  message: ConversationMessage;
  rows?: number;
  insertControl?: (append: (content: string) => void) => ReactNode;
  uploadImage?: InlineImageUploader;
  previewImage?: InlineImagePreviewer;
  onCancel: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [known] = useState(() => attachedImages(message));
  const [images, setImages] = useState<InlineImage[]>([]);
  const [initialBody] = useState(() =>
    refreshInlineImages(
      toEditorMarkdown(message.body, message.bodyFormat),
      known,
      Date.now() + URL_REFRESH_MARGIN_MS,
    ),
  );
  const [body, setBody] = useState(initialBody);
  const [kept, setKept] = useState(message.attachments);
  const [templateInsertedAt, setTemplateInsertedAt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refs = new Map([
    ...Array.from(known, ([id, url]) => [url, id] as const),
    ...images.map((i) => [i.url, i.id] as const),
  ]);
  const trimmed = deflateInlineImages(body, refs).trim();
  const inlined = new Set(inlineImageIds(trimmed));
  const filed = kept.filter((a) => !inlined.has(a.id));
  const draft = useAttachmentDraft(filed.length);
  const tooLong = trimmed.length > MESSAGE_BODY_MAX;
  const attachmentCount = filed.length + draft.files.length;
  const saveDisabled = busy || trimmed.length === 0 || tooLong;
  const removedAny = kept.length !== message.attachments.length;
  const initialStored = deflateInlineImages(initialBody, refs).trim();
  const dirty = !sameBody(trimmed, initialStored) || removedAny || draft.files.length > 0;

  async function upload(file: File): Promise<InlineImage> {
    if (!uploadImage) throw new Error("Images are not supported here.");
    const image = await uploadImage(file);
    setImages((prev) => [...prev, image]);
    return image;
  }

  function removeKept(attachmentId: string) {
    setKept((prev) => prev.filter((a) => a.id !== attachmentId));
    draft.setError(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || saveDisabled) return;
    if (!dirty) {
      onCancel();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await conversationsApi.saveMessageEdit(message.id, {
        body: trimmed,
        bodyFormat: "Markdown",
        keptAttachmentIds: removedAny ? kept.map((a) => a.id) : null,
        files: draft.files,
      });
      await onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403 ? err.message : MESSAGE_EDIT_ERROR,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      {insertControl && (
        <div className="mb-2">
          {insertControl((content) => {
            setBody((prev) => appendMarkdown(prev, content));
            setTemplateInsertedAt((n) => n + 1);
          })}
        </div>
      )}

      <RichTextEditor
        autoFocus
        revealEndSignal={templateInsertedAt}
        value={body}
        onChange={setBody}
        invalid={tooLong}
        compact={rows <= 2}
        placeholder="Edit your message…"
        uploadImage={uploadImage ? upload : undefined}
        previewImage={previewImage}
      />

      {tooLong && <p className="mt-2 text-xs text-red-600">{MESSAGE_TOO_LONG_ERROR}</p>}

      {attachmentCount > 0 && (
        <div className="mt-3 space-y-3">
          {busy && draft.files.length > 0 && (
            <div className="rounded-md border border-primary bg-primary/10 px-4 py-3">
              <BusyIndicator label="Uploading…" />
            </div>
          )}
          <AttachmentGrid>
            {filed.map((a) => (
              <AttachmentTile
                key={a.id}
                filename={a.fileName}
                contentType={a.contentType}
                sizeBytes={a.sizeBytes}
                disabled={busy}
                onRemove={() => removeKept(a.id)}
              />
            ))}
            {draft.files.map((file, index) => (
              <AttachmentTile
                key={`${file.name}-${index}`}
                filename={file.name}
                contentType={file.type}
                sizeBytes={file.size}
                disabled={busy}
                onRemove={() => draft.removeFile(index)}
              />
            ))}
          </AttachmentGrid>
        </div>
      )}

      {draft.error && <p className="mt-2 text-xs text-red-600">{draft.error}</p>}
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

      <input
        ref={draft.inputRef}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        className="hidden"
        onChange={(e) => draft.addFiles(e.target.files)}
      />

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={draft.openFilePicker}
          disabled={busy}
          className="flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline disabled:opacity-50"
        >
          <Paperclip className="h-4 w-4" />
          Attach
        </button>

        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={saveDisabled}>
            {busy && <Spinner />}
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </form>
  );
}

function sameBody(a: string, b: string): boolean {
  return a.replace(/\n{2,}/g, "\n").trim() === b.replace(/\n{2,}/g, "\n").trim();
}

function attachedImages(message: ConversationMessage): Map<string, string> {
  const map = new Map<string, string>();
  for (const a of message.attachments) {
    if (a.url) map.set(a.id, a.url);
  }
  return map;
}
